/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Typography2, typography2 } from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SponsoredBadge from 'bundles/search-common/components/search-cards/SponsoredBadge';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { buildTakeoverTrackingPayload } from 'bundles/search-common/experiments/takeoverPilot/takeoverPilotTracking';
import { isSafeAssetUrl } from 'bundles/search-common/utils/isSafeAssetUrl';

const CARD_MIN_HEIGHT = 275;

const HERO_IMAGE_MAX_WIDTH = 320;
const HERO_IMAGE_MAX_HEIGHT = 277;
const HERO_IMAGE_ASPECT_RATIO = `${HERO_IMAGE_MAX_WIDTH} / ${HERO_IMAGE_MAX_HEIGHT}`;

const HERO_IMAGE_PARAMS = { fit: 'crop', quality: '50', auto: 'format, compress, enhance' };

const HERO_IMAGE_FULL_BLEED_PARAMS = { fit: 'clip', quality: '50', auto: 'format, compress, enhance' };

const PARTNER_LOGO_MAX_HEIGHT = 24;
const PARTNER_LOGO_MAX_WIDTH = 120;
const PARTNER_LOGO_PARAMS = { fit: 'clip', quality: '50', auto: 'format, compress, enhance' };

const HEADLINE_LINE_CLAMP = 3;
const DESCRIPTION_LINE_CLAMP = 2;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    min-height: ${CARD_MIN_HEIGHT}px;
    height: 100%;
    border-radius: var(--cds-spacing-200);
    overflow: hidden;
    background-color: var(--cds-color-darken-950);
    transition: all 0.3s cubic-bezier(0, 0, 0.5, 1);

    &:hover {
      box-shadow:
        0 0 4px var(--cds-color-grey-50),
        0 4px 12px 4px var(--cds-color-darken-100);
      transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;

      &:hover {
        transform: none;
      }
    }
  `,
  cardLink: css`
    color: inherit;
    cursor: pointer;
    text-decoration: none;

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  listContainer: css`
    flex-direction: column;
    min-height: unset;
    align-items: stretch;
    padding: var(--cds-spacing-150);
    gap: var(--cds-spacing-200);
  `,
  headline: css`
    ${typography2.titleXSmall};
    display: -webkit-box;
    -webkit-line-clamp: ${HEADLINE_LINE_CLAMP};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  description: css`
    display: -webkit-box;
    -webkit-line-clamp: ${DESCRIPTION_LINE_CLAMP};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-300) var(--cds-spacing-300) 0;
  `,
  listContent: css`
    padding: 0;
  `,
  partnerLogo: css`
    height: ${PARTNER_LOGO_MAX_HEIGHT}px;
    width: auto;
    margin-right: auto;
  `,
  ctaButton: css`
    ${typography2.actionSecondary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    align-self: flex-start;
    margin-top: var(--cds-spacing-100);
    max-width: 320px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-100);
    color: var(--cds-color-interactive-primary);
    background-color: var(--cds-color-neutral-background-primary);
    text-align: center;
  `,
  heroImageWrapper: css`
    position: relative;
    width: 100%;
    max-height: ${HERO_IMAGE_MAX_HEIGHT}px;
    aspect-ratio: ${HERO_IMAGE_ASPECT_RATIO};
    overflow: hidden;
    margin-top: var(--cds-spacing-100);

    img {
      display: block;
      width: 100%;
      height: 100%;
      max-width: none !important;
      max-height: none !important;
      object-fit: cover;
      object-position: right center;
    }
  `,
  // No forced aspect-ratio/cropping -- the full-bleed asset's own natural shape is the point.
  heroImageWrapperFullBleed: css`
    position: relative;
    width: 100%;
    max-height: ${HERO_IMAGE_MAX_HEIGHT}px;
    margin-top: var(--cds-spacing-100);
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: auto;
      max-width: none !important;
      max-height: none !important;
      object-position: right center;
    }
  `,
  footer: css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: var(--cds-spacing-150) var(--cds-spacing-300);
  `,
  listFooter: css`
    padding: 0;
  `,
};

export type TakeoverData = {
  campaignId: string;
  partnerId?: string;
  placementId?: string;
  partnerName?: string;
  headline: string;
  description?: string;
  ctaLabel: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  // When true, the partner logo is baked into heroImageUrl, so the separate logo is skipped
  // and the image keeps its natural aspect ratio. The Sponsored badge still always renders.
  heroImageIsFullBleed?: boolean;
  partnerLogoUrl?: string;
  backgroundColor?: string;
};

type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  id?: string;
  name?: string;
  slug?: string;
  productType?: LearningProduct;
  linkProps?: { href?: string; target?: string; component?: React.ElementType };
  cardIndex?: number;
  searchedQuery?: string;
  takeoverData: TakeoverData;
  variant?: 'list' | 'grid';
};

const TakeoverCard = ({
  id,
  name,
  slug,
  productType,
  linkProps,
  cardIndex,
  searchedQuery,
  takeoverData,
  variant = 'grid',
}: Props) => {
  const track = useTracker();
  const {
    campaignId,
    partnerId,
    placementId,
    partnerName,
    headline,
    description,
    ctaLabel,
    heroImageUrl,
    heroImageAlt,
    heroImageIsFullBleed,
    partnerLogoUrl,
    backgroundColor,
  } = takeoverData;

  const buildTrackingPayload = () =>
    buildTakeoverTrackingPayload({
      campaignId,
      partnerId,
      placementId,
      cardIndex,
      id,
      name,
      slug,
      productType,
      searchedQuery,
    });

  const viewTrackingData: EventData<'view_takeover_card'> = buildTrackingPayload();

  const visibilityRef = useVisibilityTracker<HTMLElement, 'view_takeover_card'>(
    'view_takeover_card',
    viewTrackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  // Only reached when no ancestor intercepts the click. Under the XDP modal,
  // CourseCardXdpTrigger stops propagation and fires this event itself instead.
  const handleClick = () => {
    const clickTrackingData: EventData<'click_takeover_card'> = buildTrackingPayload();
    track('click_takeover_card', clickTrackingData);
  };

  const safeHref = isSafeAssetUrl(linkProps?.href) ? linkProps?.href : undefined;
  const safeHeroImageUrl = isSafeAssetUrl(heroImageUrl) ? heroImageUrl : undefined;
  const safePartnerLogoUrl = isSafeAssetUrl(partnerLogoUrl) ? partnerLogoUrl : undefined;

  const LinkComponent = linkProps?.component;
  const relForTarget = linkProps?.target === '_blank' ? 'noopener noreferrer' : undefined;

  const logoContent = !heroImageIsFullBleed && safePartnerLogoUrl && (
    <Imgix
      src={safePartnerLogoUrl}
      alt={partnerName ?? ''}
      css={styles.partnerLogo}
      maxHeight={PARTNER_LOGO_MAX_HEIGHT}
      maxWidth={PARTNER_LOGO_MAX_WIDTH}
      imgParams={PARTNER_LOGO_PARAMS}
      loading="lazy"
    />
  );

  const headlineContent = (
    <Typography2 variant="titleSmall" component="h3" color="invertBody" css={styles.headline}>
      {headline}
    </Typography2>
  );

  const descriptionContent = description && (
    <Typography2 variant="bodyPrimary" component="p" color="invertBody" css={styles.description}>
      {description}
    </Typography2>
  );

  const ctaContent = safeHref && (
    <span css={styles.ctaButton} data-testid="takeover-card-cta">
      {ctaLabel}
    </span>
  );

  const heroImageContent = safeHeroImageUrl && (
    <Imgix
      src={safeHeroImageUrl}
      alt={heroImageAlt ?? headline}
      maxWidth={HERO_IMAGE_MAX_WIDTH}
      maxHeight={HERO_IMAGE_MAX_HEIGHT}
      {...(heroImageIsFullBleed ? { imgParams: HERO_IMAGE_FULL_BLEED_PARAMS } : { imgParams: HERO_IMAGE_PARAMS })}
      loading="lazy"
    />
  );

  // Always rendered -- the "Sponsored" disclosure isn't reliably baked into full-bleed assets.
  const footerContent = (
    <div css={[styles.footer, variant === 'list' && styles.listFooter]}>
      <SponsoredBadge color="invertBody" />
    </div>
  );

  const CardComponent = safeHref ? (LinkComponent ?? 'a') : 'div';
  const cardLinkProps = safeHref
    ? {
        href: safeHref,
        target: linkProps?.target,
        rel: relForTarget,
        onClick: handleClick,
      }
    : {};
  const cardVisibilityRefProps = safeHref && LinkComponent ? { refAlt: visibilityRef } : { ref: visibilityRef };

  if (variant === 'list') {
    return (
      <CardComponent
        {...cardLinkProps}
        {...cardVisibilityRefProps}
        css={[styles.container, safeHref && styles.cardLink, styles.listContainer]}
        style={backgroundColor ? { backgroundColor } : undefined}
        data-testid="takeover-card"
      >
        <div css={[styles.content, styles.listContent]}>
          {logoContent}
          {headlineContent}
          {descriptionContent}
          {ctaContent}
        </div>
        {heroImageContent && (
          <div css={heroImageIsFullBleed ? styles.heroImageWrapperFullBleed : styles.heroImageWrapper}>
            {heroImageContent}
          </div>
        )}
        {footerContent}
      </CardComponent>
    );
  }

  return (
    <CardComponent
      {...cardLinkProps}
      {...cardVisibilityRefProps}
      css={[styles.container, safeHref && styles.cardLink]}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid="takeover-card"
    >
      <div css={styles.content}>
        {logoContent}
        {headlineContent}
        {descriptionContent}
        {ctaContent}
      </div>

      {heroImageContent && (
        <div css={heroImageIsFullBleed ? styles.heroImageWrapperFullBleed : styles.heroImageWrapper}>
          {heroImageContent}
        </div>
      )}

      {footerContent}
    </CardComponent>
  );
};

export default TakeoverCard;
